/**
 * The State filings screens' ONE client module: the list, Upcoming and detail reads, and
 * the writes of the console's State filings exception (owner answer 7, spec
 * 2026-09-30-state-tax-refactor section 4.1 and 5.6): start filing, mark filed,
 * upload proof, confirm the Delaware amount, record a wire, change the tax year
and close. The
 * Worker side is `src/worker/state-filings-writes.ts`.
 *
 * The writes are same-origin `fetch` with `credentials: "same-origin"`: the
 * deciding credential is the operator's HttpOnly session cookie, never a
 * bearer, and each carries the CSRF evidence the Worker demands, the custom
 * `X-Admin-State-Filing-Write: 1` header and a JSON body. Types come from
 * `@nt/state-tax-rules` (`AdminStateTaxListRow`, `AdminStateTaxDetail`, the
 * `Admin*Request` bodies), the same definitions the API serialises.
 */
import type {
  AdminAddProofRequest,
  AdminCloseRequest,
  AdminConfirmQuoteRequest,
  AdminMarkFiledRequest,
  AdminReadProofRequest,
  AdminReadProofResponse,
  AdminRecordPaymentRequest,
  AdminRelabelTaxYearRequest,
  AdminSendBackRequest,
  AdminStateTaxDetail,
  AdminStateTaxListRow,
  AdminStateTaxUpcomingRow,
  AdminStateTaxUpcomingWindow,
  CustomerDocumentLink,
} from "@nt/state-tax-rules";

import { apiGet } from "@/lib/api";
import { DETAIL, LIVE_LIST } from "@/lib/query-freshness";
import type { ListResponse } from "@/types";

export type {
  AdminStateTaxDetail,
  AdminStateTaxListRow,
  AdminStateTaxUpcomingRow,
  AdminStateTaxUpcomingWindow,
} from "@nt/state-tax-rules";

const STATE_FILINGS_PAGE_SIZE = 20;

export const STATE_FILING_NEEDS = [
  "quote_confirmation",
  "filing",
  "proof",
  "payment",
] as const;
export type StateFilingNeed = (typeof STATE_FILING_NEEDS)[number];

/** The Orders view's first tab and default: every order that is ours to move (`needs=ours`). */
export const NEEDS_US_TAB = "needs_us";
/** The tab that lists every order. Not "", because a tab of "" is the URL's way to say "the default". */
export const ALL_TAB = "all";

/**
 * The list filters one Orders tab stands for. An absent or unknown tab is the default (Needs us), the
 * same rule `AdminListPage` applies to the tab it shows; any other tab is a rulebook status.
 */
export function stateFilingsTabFilters(
  tab: string | null,
  statuses: readonly string[],
): { status: string; needs: string } {
  if (tab === ALL_TAB) return { status: "", needs: "" };
  if (tab && statuses.includes(tab)) return { status: tab, needs: "" };
  return { status: "", needs: "ours" };
}

export interface StateFilingsListParams {
  page: number;
  status: string;
  state: string;
  needs: string;
  companyId: string;
  /** URL-only, like `companyId`: `?taxYear=2025` narrows the queue to one year. */
  taxYear?: string;
  /** Company name, name plus designator, or company id (`?search=`). */
  search?: string;
}

/**
 * The queue: state-tax orders across every company. The API filters, sorts
 * (open orders by due date, filed and closed after) and pages; the answer is a
 * FLAT envelope, `{data: rows, total, page, pageSize, totalPages}`, which
 * `apiGet` hands over whole.
 */
export function stateFilingsListQuery(params: StateFilingsListParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "state-filings",
      params.page,
      params.status,
      params.state,
      params.needs,
      params.companyId,
      params.taxYear ?? "",
      params.search ?? "",
    ] as const,
    queryFn: () =>
      apiGet<ListResponse<AdminStateTaxListRow>>("/api/admin/state-filings", {
        page: params.page,
        pageSize: STATE_FILINGS_PAGE_SIZE,
        status: params.status || undefined,
        state: params.state || undefined,
        needs: params.needs || undefined,
        company_id: params.companyId || undefined,
        taxYear: params.taxYear || undefined,
        search: params.search?.trim() || undefined,
      }),
  };
}

const STATE_FILINGS_UPCOMING_PAGE_SIZE = 25;

/** The Upcoming view's window: overdue plus the next N days, or everything. */
export const UPCOMING_WINDOWS: ReadonlyArray<{
  value: AdminStateTaxUpcomingWindow;
  label: string;
}> = [
  { value: "30", label: "Overdue + next 30 days" },
  { value: "90", label: "Overdue + next 90 days" },
  { value: "180", label: "Overdue + next 180 days" },
  { value: "all", label: "All upcoming" },
];
export const DEFAULT_UPCOMING_WINDOW: AdminStateTaxUpcomingWindow = "90";

export interface StateFilingsUpcomingParams {
  page: number;
  /** `WY`, `DE`, or "" for both. */
  state: string;
  window: AdminStateTaxUpcomingWindow;
  /** Company name, name plus designator, or company id. */
  search: string;
}

/** The Upcoming answer: the list's flat envelope plus the companies left out. */
export interface StateFilingsUpcomingResponse extends ListResponse<AdminStateTaxUpcomingRow> {
  /** Wyoming and Delaware companies with no formation date known (ours or the state's). */
  withoutFormationDate?: number;
}

/**
 * Upcoming: every Wyoming and Delaware company's NEXT state filing (the
 * earliest owed year the state does not show filed), soonest due first. The
 * API computes the year with the customer dashboard's own rule, filters, sorts
 * and pages; the answer is the list's flat envelope.
 */
export function stateFilingsUpcomingQuery(params: StateFilingsUpcomingParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "state-filings-upcoming",
      params.page,
      params.state,
      params.window,
      params.search,
    ] as const,
    queryFn: () =>
      apiGet<StateFilingsUpcomingResponse>(
        "/api/admin/state-filings/upcoming",
        {
          page: params.page,
          pageSize: STATE_FILINGS_UPCOMING_PAGE_SIZE,
          state: params.state || undefined,
          window: params.window,
          search: params.search.trim() || undefined,
        },
      ),
  };
}

/** One order: `service_data`, the state facts, the deep link, the moves it allows. */
export function stateFilingDetailQuery(orderId: string) {
  return {
    ...DETAIL,
    queryKey: ["state-filing", orderId] as const,
    queryFn: () =>
      apiGet<AdminStateTaxDetail>(
        `/api/admin/state-filings/${encodeURIComponent(orderId)}`,
      ),
  };
}

// --- writes -----------------------------------------------------------------

interface StateFilingWriteFailure extends Error {
  status: number;
  code: string | null;
}

const orderPath = (orderId: string) =>
  `/api/admin/state-filings/${encodeURIComponent(orderId)}`;

async function write(path: string, body: unknown): Promise<unknown> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-State-Filing-Write": "1",
    },
    body: JSON.stringify(body),
  });
  const result = (await response.json().catch(() => null)) as {
    data?: unknown;
    error?: { code?: string; message?: string };
  } | null;
  if (!response.ok) {
    throw Object.assign(
      new Error(
        result?.error?.message ?? `Request failed (${response.status}).`,
      ),
      { status: response.status, code: result?.error?.code ?? null },
    ) as StateFilingWriteFailure;
  }
  return result && "data" in result ? result.data : result;
}

/** paid -> in_progress. */
export const startFiling = (orderId: string) =>
  write(`${orderPath(orderId)}/start-filing`, {});

/** in_progress -> filed. The API refuses it without a proof document. */
export const markFiled = (orderId: string, input: AdminMarkFiledRequest) =>
  write(`${orderPath(orderId)}/mark-filed`, input);

/** quote_review -> awaiting_payment: the Delaware amount, checked against eCorp. */
export const confirmQuote = (
  orderId: string,
  input: AdminConfirmQuoteRequest,
) => write(`${orderPath(orderId)}/confirm-quote`, input);

/** awaiting_payment -> paid, for a wire the team saw arrive. */
export const recordPayment = (
  orderId: string,
  input: AdminRecordPaymentRequest,
) => write(`${orderPath(orderId)}/record-payment`, input);

/** quote_review -> details_needed: the customer corrects their inputs; `note` is what they read. */
export const sendBackQuote = (orderId: string, input: AdminSendBackRequest) =>
  write(`${orderPath(orderId)}/send-back`, input);

/** Carry an order that is not filed or closed to another tax year (the status stays). */
export const relabelTaxYear = (
  orderId: string,
  input: AdminRelabelTaxYearRequest,
) => write(`${orderPath(orderId)}/relabel-tax-year`, input);

/** Any open status -> closed, with a reason. */
export const closeOrder = (orderId: string, input: AdminCloseRequest) =>
  write(`${orderPath(orderId)}/close`, input);

/** Mint a PUT signature for one proof file, under the acting admin's own prefix. */
export const presignProofUpload = (
  orderId: string,
  input: { key: string; contentType: string },
): Promise<{ url: string; expiresAt: number }> =>
  write(`${orderPath(orderId)}/proof/presign`, { ...input }) as Promise<{
    url: string;
    expiresAt: number;
  }>;

/** Attach an uploaded object to the order as a proof document. */
export const recordProof = (orderId: string, input: AdminAddProofRequest) =>
  write(`${orderPath(orderId)}/proof`, input);

/**
 * Read an uploaded state document with code (no AI) and answer what it says:
 * the kind, the reference, the filed date. It writes nothing; staff confirm
 * the values before `recordProof` or `markFiled` saves anything.
 */
export const readProof = (
  orderId: string,
  input: AdminReadProofRequest,
): Promise<AdminReadProofResponse> =>
  write(
    `${orderPath(orderId)}/proof/read`,
    input,
  ) as Promise<AdminReadProofResponse>;

/**
 * A short-lived signed link for a proof document (numeric id) or an archived
 * registry report (UUID): `GET /api/admin/state-filings/documents/:documentId`.
 */
export const stateFilingDocumentLink = (documentId: string) =>
  apiGet<CustomerDocumentLink>(
    `/api/admin/state-filings/documents/${encodeURIComponent(documentId)}`,
  );

/** What an operator reads when a write fails: the reason, and what to do next. */
export function stateFilingWriteFailureMessage(error: unknown): string {
  const failure = error as Partial<StateFilingWriteFailure> | null;
  switch (failure?.status) {
    case 401:
      return "Your session ended. Sign in again, then retry.";
    case 403:
      return "The console refused this write. Reload the page and retry from this screen.";
    case 409:
      return `${failure.message ?? "The order moved on."} Reload the order to see its current state.`;
    case 503:
    case 504:
      return "The API did not answer. Reload the order to check whether it landed before retrying.";
    default:
      return `${failure?.message ?? "The action failed."} Fix the input and retry, or reload the order.`;
  }
}
